import dayjs from "dayjs";
import { PSD2_FIELD_SOURCE } from "~/utils/const";
import { PSD2_STATE, SYNC_STATUS } from "~/utils/psd2/constants";

/**
 * Turns a sync status into the screen it should produce.
 *
 * Two rules earn their keep here:
 *
 * - A status we do not recognise resolves to the failure screen, never to the
 *   intro. Silently falling through to step one is how a customer ends up asked
 *   to redo a sync they already completed.
 * - "We have nothing on record" only means "start over" when we are not already
 *   waiting on a sync. If the customer was just at their bank, the result simply
 *   has not reached us yet.
 *
 * Returning null means "leave the customer where they are" - not knowing is
 * different from knowing there is nothing.
 *
 * This was a class holding a handler map, plus a value object with five static
 * factories, for what is one switch over plain descriptions of a screen.
 */

/** A new attempt is running, so previous outcomes may be reported again. */
export const SYNC_IN_PROGRESS = Object.freeze({
    state: PSD2_STATE.IN_PROGRESS,
    resetsNotifications: true,
});

const NOTHING_YET = Object.freeze({ state: PSD2_STATE.INTRO });

const SYNCED = Object.freeze({
    state: PSD2_STATE.RESULT,
    event: "psd2_success",
    emitsSynced: true,
});

const ACCOUNT_MISMATCH = Object.freeze({
    state: PSD2_STATE.ACCOUNT_MISMATCH,
    event: "psd2_account_mismatch",
});

const failed = (event) => ({ state: PSD2_STATE.FAILED, event: event });

/**
 * A sync that predates the correction it is meant to answer does not answer it.
 * Precedence preserved from the original expression: the date check applies to
 * corrections only.
 */
function answersTheAsk(sync, source, correctionCreatedAt) {
    return source === PSD2_FIELD_SOURCE.BNPL_APPLICATION ||
        source === PSD2_FIELD_SOURCE.PRELIM ||
        (source === PSD2_FIELD_SOURCE.CORRECTION &&
            dayjs(correctionCreatedAt).isBefore(dayjs(sync.lastSyncDate)));
}

/**
 * @param sync         the latest sync the backend knows about, or null
 * @param syncInFlight whether we have our own note saying one is running
 * @returns a screen to show, or null to leave the customer where they are
 */
export function presentationFor(sync, {
    source,
    correctionCreatedAt,
    isBusiness = false,
    syncInFlight = false,
    logger = console,
} = {}) {
    if (!sync || !sync.hasSyncedBefore) {
        if (syncInFlight) {
            return SYNC_IN_PROGRESS;
        }
        if (!sync) {
            return NOTHING_YET;
        }
        // A sync exists but is not usable - an expired one, say. Nothing to
        // show, but nothing to undo either.
        return null;
    }

    switch (sync.lastSyncStatus) {
        case SYNC_STATUS.SUCCESS:
            if (answersTheAsk(sync, source, correctionCreatedAt)) {
                return SYNCED;
            }
            // An older success of theirs. The sync we are waiting on is not in.
            return syncInFlight ? SYNC_IN_PROGRESS : null;

        case SYNC_STATUS.IN_PROGRESS:
        case SYNC_STATUS.LITERALLY_IN_PROGRESS:
            return SYNC_IN_PROGRESS;

        case SYNC_STATUS.FAILED:
            return failed("psd2_failed");

        // The customer is shown the generic failure screen: the reason this
        // sync was rejected stays internal.
        case SYNC_STATUS.FRAUD:
            return failed("psd2_fraud");

        case SYNC_STATUS.BANK_ACCOUNT_MISMATCH:
            // There are no bnpl: tokens for the mismatch copy yet, so a
            // consumer gets the generic failure screen, which is what they have
            // always seen. The event is reported either way. Drop the branch
            // once the tokens exist.
            return isBusiness ? ACCOUNT_MISMATCH : failed("psd2_account_mismatch");

        default:
            logger.log("PSD2:: unhandled sync status", sync.lastSyncStatus);
            return failed("psd2_unhandled_status");
    }
}
